import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { Avatar, Box, Button, HStack, Text, VStack } from '@bonusly/design-system';

import { AvatarGroup } from '../../design-system/avatar/components/avatar-group';

interface Requester {
	id: string;
	name: string;
	profilePic: string;
}

interface TeamRequestBannerProps {
	requesters: Requester[];
	variant?: 'full' | 'compact';
	onClick?: () => void;
}

export const TeamRequestBanner: FC<TeamRequestBannerProps> = ({ requesters, variant = 'full', onClick }) => {
	const { t } = useTranslation('team_setup', { keyPrefix: 'banner' });

	if (requesters.length === 0) return null;

	const isSingle = requesters.length === 1;

	if (variant === 'compact') {
		return (
			<Box bg="bg.surface" borderRadius="md" p={3}>
				<VStack align="flex-start" spacing={2}>
					<HStack gap={2} alignItems="center">
						<AvatarGroup max={3} size="sm">
							{requesters.map((r) => (
								<Avatar key={r.id} name={r.name} src={r.profilePic} size="sm" />
							))}
						</AvatarGroup>
						<Text fontSize="sm" fontWeight="medium" color="text.default" noOfLines={2} flex={1}>
							{isSingle ? t('compactSingleRequest', { name: requesters[0].name }) : t('compactMultipleRequests', { count: requesters.length })}
						</Text>
					</HStack>
					<Button as={RouterLink} to="/team/configure" colorScheme="primary" size="sm" w="100%" onClick={onClick}>
						{isSingle ? t('reviewRequest') : t('reviewRequests')}
					</Button>
				</VStack>
			</Box>
		);
	}

	return (
		<VStack align="flex-start" spacing={3}>
			<Box>
				<Text fontSize="md" fontWeight="semibold" color="text.default">
					{isSingle ? t('singleRequestTitle') : t('multipleRequestsTitle')}
				</Text>
				<Text fontSize="sm" color="text.soft" mt={1}>
					{isSingle ? t('singleRequestDescription', { name: requesters[0].name }) : t('multipleRequestsDescription', { count: requesters.length })}
				</Text>
			</Box>
			<HStack justify="space-between" w="100%">
				<AvatarGroup max={4} size="sm">
					{requesters.map((r) => (
						<Avatar key={r.id} name={r.name} src={r.profilePic} size="sm" />
					))}
				</AvatarGroup>
				<Button as={RouterLink} to="/team/configure" colorScheme="primary" size="sm" onClick={onClick}>
					{isSingle ? t('reviewRequest') : t('reviewRequests')}
				</Button>
			</HStack>
		</VStack>
	);
};
